<template>
  <div>
    <b-container v-if="!loadingModal.dialog" class="justify-content-center">
      <div v-if="showBanner" class="d-flex justify-content-center">
        <b-img :src="banner" @error="showBanner = false" fluid-grow />
      </div>
      <PaymentError v-if="paymentError" :error="paymentError" />

      <!-- Receipt Details -->
      <b-row>
        <b-col cols="12">
          <PaymentReceipt
            v-if="receiptInformation"
            :merchantId="paymentConfig.merchantId"
            :isRecurring="paymentConfig.isRecurring"
            :recurringInfo="recurringInfo"
            :receiptDetails="generatedReceipt"
          />
        </b-col>
      </b-row>
      <b-row>
        <!-- Return Button -->
        <b-col
          v-if="this.paymentConfig.returnToMerchantUrl"
          class="return-btn-div"
        >
          <v-btn class="return-btn" x-large @click="backToMerchant">Done</v-btn>
        </b-col>
      </b-row>
    </b-container>
    <LoadingModal v-bind="loadingModal" />
    <AlertModal
      v-bind="alertModal"
      v-model="alertModal.show"
      @dismiss="resetAlert"
    />
  </div>
</template>
<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap");
.return-btn-div {
  margin: 0 0 3rem;
}
.return-btn {
  background: #625ee8 !important;
  height: 24px;
  width: 300px;
  color: #ffffff;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 24px;
  text-align: center;
  width: 100%;
}
@media screen and (max-width: 990px) {
  .return-btn-div {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .return-btn {
    width: 100%;
  }
}
</style>

<script>
import { API, graphqlOperation, Storage } from "aws-amplify";
import PaymentReceipt from "@/components/payment/PaymentReceipt.vue";
import PaymentError from "@/components/payment/PaymentError.vue";
import LoadingModal from "@/components/widgets/LoadingModal.vue";
import AlertModal from "@/components/widgets/AlertModal.vue";
import { generatePaymentPageQueryObject } from "@/services/MerchantSiteInteractionService.js";
import {
  CLEAR_PAYMENT_CONFIG,
  SAVE_PAYMENT_CONFIG,
  SAVE_TRANSACTION_ERROR
} from "@/store/payment.module";
import AppSettings from "@/appsettings/appsettings.js";

export default {
  components: {
    PaymentReceipt,
    PaymentError,
    LoadingModal,
    AlertModal
  },
  data() {
    return {
      recurringInfo: {},
      receiptInformation: null,
      paymentConfig: {
        merchantId: null,
        returnToMerchantUrl: null,
        intentId: null,
        amount: null,
        currencyText: null,
        orderNumber: null,
        isRecurring: false
      },
      showBanner: false,
      banner: null,
      paymentError: null,
      transactionResultId: "",
      loadingModal: {
        dialog: true,
        text: "Loading Receipt, Please Wait"
      },
      alertModal: {
        show: false,
        text: "",
        type: AlertModal.INFORMATION
      }
    };
  },
  computed: {
    generatedReceipt() {
      return {
        ...this.receiptInformation,
        amount: this.paymentConfig.amount,
        currencyText: this.paymentConfig.currencyText,
        orderNumber: this.paymentConfig.orderNumber,
        receiptNumber: this.paymentConfig.orderNumber
      };
    },
    isAuthenticated() {
      return this.$store.getters.isAuthenticated;
    },
    authMode() {
      return this.isAuthenticated ? null : AppSettings.UnauthenticatedAuthMode;
    }
  },
  methods: {
    async retrievePaymentInfo(id) {
      const getTransactionResult = `
              query GetTransactionResult(
                $params: QueryGetTransactionResultParamsInput!
              ) {
                getTransactionResult(params: $params) {
                  approved,
                  description,
                  recurringInfo{
                    recurringAmount
                    recurringOriginalAmount
                    executionDate
                    frequency
                    paymentDescription
                    period
                  },
                  detail{
                    orderNumber
                    authorizationNumber
                    transactionDate
                    amount
                    originalAmount
                    discounts{
                      Name
                      Amount
                    }
                    currencyText
                    tenantAlias
                    intentId
                    paddedCardNumber
                    firstName
                    lastName
                    email
                    returnUrl
                    isRecurring
                  }
                }
              }
            `;
      const params = {
        id
      };
      const transactionResultResponse = await API.graphql({
        ...graphqlOperation(getTransactionResult, { params }),
        authMode: this.authMode
      });
      return transactionResultResponse.data.getTransactionResult;
    },
    handleTransactionResultResponse(transactionResult) {
      if (transactionResult.recurringInfo)
        this.recurringInfo = this.formatRecurringInfo(
          transactionResult.recurringInfo
        );
      this.setPaymentConfig(transactionResult.detail);
      transactionResult.approved
        ? this.populateReceiptInfo(transactionResult.detail)
        : this.setPaymentError(transactionResult);
    },
    setPaymentConfig(config) {
      if (config) {
        this.paymentConfig.intentId = config.intentId;
        this.paymentConfig.merchantId = config.tenantAlias;
        this.paymentConfig.returnToMerchantUrl = config.returnUrl;
        this.paymentConfig.amount = config.amount;
        this.paymentConfig.currencyText = config.currencyText;
        this.paymentConfig.orderNumber = config.orderNumber;
        this.paymentConfig.isRecurring = config.isRecurring;
      }
    },
    populateReceiptInfo(paymentInfo) {
      this.receiptInformation = {
        name: this.fullName(paymentInfo.firstName, paymentInfo.lastName),
        email: paymentInfo.email,
        authNumber: paymentInfo.authorizationNumber,
        date: paymentInfo.transactionDate,
        paddedCardNumber: paymentInfo.paddedCardNumber,
        discounts: paymentInfo.discounts,
        originalAmount: paymentInfo.originalAmount
      };
      this.loadingModal.dialog = false;
    },
    setPaymentError(transactionResult) {
      const errorDescription = transactionResult.description;
      this.paymentError = {
        errorTitle: "Transaction Declined",
        errorDescription
      };
      const intentId = this.paymentConfig.intentId;
      if (!intentId) {
        this.loadingModal.dialog = false;
        return;
      }
      this.savePaymentConfigToVuex(transactionResult.detail);
      this.saveTransactionError(this.paymentError);
      this.sendToPage(intentId, transactionResult.detail);
    },
    sendToPage(intentId, transactionDetails) {
      if (!transactionDetails.isRecurring) {
        this.sendToPaymentPage(intentId);
      } else {
        this.sendToRecurringPaymentPage(intentId);
      }
    },
    sendToPaymentPage(id) {
      if (!id) {
        this.loadingModal.dialog = false;
        return;
      }
      this.$router.push({
        name: "payment",
        params: { id }
      });
    },
    sendToRecurringPaymentPage(id) {
      if (!id) {
        this.loadingModal.dialog = false;
        return;
      }
      this.$router.push({ name: "recurring-payment", params: { id } });
    },
    generatePaymentPageQueryParam(transactionDetails) {
      try {
        const orderNumber = transactionDetails.orderNumber;
        const amount = transactionDetails.originalAmount;
        const returnUrl = transactionDetails.returnUrl;

        return generatePaymentPageQueryObject(orderNumber, amount, returnUrl);
      } catch (e) {
        this.$logger.error(e);
      }
      return {};
    },
    fullName(firstName, lastName) {
      return `${firstName} ${lastName}`;
    },
    async retrieveBanner() {
      if (
        this.paymentConfig.merchantId == undefined ||
        this.paymentConfig.merchantId == null
      )
        return;
      const vm = this;
      Storage.get(`merchant/banner/${this.paymentConfig.merchantId}`)
        .then(async result => {
          vm.banner = result;
          vm.showBanner = true;
        })
        .catch(err => {
          vm.$logger.error(e);
          vm.banner = null;
        });
    },
    packageOrderInformation(paymentInfo) {
      if (!paymentInfo) return null;
      return {
        firstName: paymentInfo.firstName,
        lastName: paymentInfo.lastName,
        email: paymentInfo.email
      };
    },
    clearPaymentConfig() {
      this.$store.dispatch(CLEAR_PAYMENT_CONFIG);
    },
    savePaymentConfigToVuex(paymentInfo) {
      const orderInformation = this.packageOrderInformation(paymentInfo);
      const fromReceiptPage = true;
      this.$store.dispatch(SAVE_PAYMENT_CONFIG, {
        paymentConfig: {
          ...this.paymentConfig,
          orderInformation
        },
        fromReceiptPage
      });
    },
    saveTransactionError(paymentError) {
      this.$store.dispatch(SAVE_TRANSACTION_ERROR, { ...paymentError });
    },
    backToMerchant() {
      this.clearPaymentConfig();
      window.location = this.paymentConfig.returnToMerchantUrl;
    },
    alert(text, type) {
      if (text) this.alertModal.text = text;
      if (type) this.alertModal.type = type;
      if (this.alertModal.text) this.alertModal.show = true;
    },
    resetAlert() {
      this.alertModal.text = "";
      this.alertModal.type = AlertModal.INFORMATION;
    },
    formatRecurringInfo(recurringInfo) {
      return {
        recurringAmount: recurringInfo.recurringAmount,
        recurringOriginalAmount: recurringInfo.recurringOriginalAmount,
        executionDate: recurringInfo.executionDate,
        frequency: recurringInfo.frequency,
        period: recurringInfo.period,
        paymentDescription: recurringInfo.paymentDescription
      };
    }
  },
  async created() {
    this.transactionResultId = this.$route.params.id;
    if (!this.transactionResultId) this.$router.push({ name: "404" });
    try {
      this.clearPaymentConfig();
      let response = await this.retrievePaymentInfo(this.transactionResultId);
      this.handleTransactionResultResponse(response);
    } catch (e) {
      this.$logger.error(e);
      this.alert(
        "There was an error retrieving your receipt.",
        AlertModal.ERROR
      );
      this.$router.push({ name: "404" });
    }
  }
};
</script>
